<template>
  <div class="ion-padding">
    <ion-chip
        v-if="item?.display_name"
        color="primary"
        @click="back_to_step ? (currentStep = 0) : {}"
    >
      <ion-icon :icon="wrenchIcon"/>
      <ion-label>{{ item.display_name }}</ion-label>
    </ion-chip>
    <ion-chip
        v-if="tracked_event"
        @click="back_to_step ? (currentStep = 1) : {}"
    >
      <ion-icon :icon="deliveryIcon"/>
      <ion-label>{{
          $t(`equipment.tracker.events.${tracked_event}`)
        }}
      </ion-label>
    </ion-chip>
    <ion-chip
        v-if="site?.display_name"
        color="secondary"
        @click="back_to_step ? (currentStep = 2) : {}"
    >
      <ion-icon :icon="mapIcon"/>
      <ion-label>{{ site.display_name }}</ion-label>
    </ion-chip>
    <ion-chip
        v-if="used_user?.display_name"
        color="tertiary"
        @click="back_to_step ? (currentStep = 3) : {}"
    >
      <ion-icon :icon="userIcon"/>
      <ion-label>{{ used_user.display_name }}</ion-label>
    </ion-chip>
    <ion-chip v-if="photos?.length" color="success" @click="currentStep = 4">
      <ion-icon :icon="cameraIcon"/>
      <ion-label>{{ photos.length }}</ion-label>
    </ion-chip>
  </div>

  <div
      v-show="currentStep === 0"
      :class="{ 'ion-padding': true, [$style['step-panel']]: true }"
  >
    <ion-icon :class="$style['step-icon']" :icon="wrenchIcon"/>

    <h1 class="ion-text-center">
      {{ $t("equipment.tracker.equipment_name") }}
    </h1>

    <EquipmentSelectorButton
        :selectedItem="item"
        @change="item = $event?.value"
    />

    <ion-button :disabled="!item" fill="clear" @click="currentStep++">
      {{ $t("equipment.tracker.next") }}
    </ion-button>
  </div>

  <div
      v-show="currentStep === 1"
      :class="{ 'ion-padding': true, [$style['step-panel']]: true }"
  >
    <div v-if="last_tracked_event_loading" :class="$style.loader">
      <ion-spinner/>
    </div>

    <ion-icon
        v-if="!last_tracked_event_loading"
        :class="$style['step-icon']"
        :icon="deliveryIcon"
    />

    <h1 v-if="!last_tracked_event_loading" class="ion-text-center">
      {{ $t("equipment.tracker.event") }}
    </h1>

    <ion-radio-group
        v-if="!last_tracked_event_loading"
        :disabled="!!(trackEvents?.length <= 1 && tracked_event)"
        :value="tracked_event"
        @ion-change="tracked_event = $event.detail.value"
    >
      <ion-radio
          v-for="trackEvent in trackEvents"
          :key="trackEvent"
          :value="trackEvent"
      >
        {{ $t(`equipment.tracker.events.${trackEvent}`) }}
      </ion-radio>
    </ion-radio-group>

    <ion-button
        v-if="!last_tracked_event_loading"
        :disabdled="!tracked_event"
        fill="clear"
        @click="currentStep++"
    >
      {{ $t("equipment.tracker.next") }}
    </ion-button>
  </div>

  <div
      v-show="currentStep === 2"
      :class="{ 'ion-padding': true, [$style['step-panel']]: true }"
  >
    <ion-icon :class="$style['step-icon']" :icon="mapIcon"/>

    <h1 class="ion-text-center">
      {{ $t("equipment.tracker.site") }}
    </h1>

    <SiteSelectorButton :selectedItem="site" @change="site = $event?.value"/>

    <ion-button :disabled="!site" fill="clear" @click="currentStep++">
      {{ $t("equipment.tracker.next") }}
    </ion-button>
  </div>

  <div
      v-show="currentStep === 3"
      :class="{ 'ion-padding': true, [$style['step-panel']]: true }"
  >
    <ion-icon :class="$style['step-icon']" :icon="userIcon"/>

    <h1 class="ion-text-center">
      {{ $t("equipment.tracker.user") }}
    </h1>

    <UserSelectorButton
        :selectedItem="used_user"
        @change="used_user = $event?.value"
    />

    <ion-button :disabled="!used_user" fill="clear" @click="currentStep++">
      {{ $t("equipment.tracker.next") }}
    </ion-button>
  </div>

  <div
      v-show="currentStep === 4"
      :class="{ 'ion-padding': true, [$style['step-panel']]: true }"
  >
    <ion-icon :class="$style['step-icon']" :icon="cameraIcon"/>

    <h1 class="ion-text-center">
      {{ $t("equipment.tracker.photos") }}
    </h1>

    <uploadable-file
        :params="{ type: 'equipment-state' }"
        :value="photos"
        multiple="true"
        @change="photos = $event"
    />

    <ion-button
        :disabled="tracked_event === 'check:in' && !photos?.length"
        fill="clear"
        @click="currentStep++"
    >
      {{ $t("equipment.tracker.next") }}
    </ion-button>
  </div>

  <div
      v-show="currentStep === 5"
      :class="{ 'ion-padding': true, [$style['step-panel']]: true }"
  > 
    <ion-icon v-show="false" :class="$style['step-icon']" :icon="chatBubbleIcon"/>
 
    <h2 class="ion-text-center">
      {{ $t("equipment.tracker.comment") }}
    </h2>

    <ion-textarea
        v-model="comment"
        :class="$style['comment']"
        :placeholder="$t('equipment.tracker.comment_placeholder')"
    ></ion-textarea>

    <h1 class="ion-text-center">
      {{ $t("equipment.tracker.sign.title") }}
    </h1>

    <vue-drawing-canvas
      :class="[canvaError ? $style['sign-bloc-error'] : $style['sign-bloc']]"
      ref="VueCanvasDrawing"
      v-model:image="signature"
      width="300"
      height="150"
    />
    <div :class="$style['sign-bloc-button']">
      <ion-button
        :class="$style['sign-bloc-clear-button']"
        color="danger"
        expand="block"
        fill="clear"
        size="default"
        @click.prevent="$refs.VueCanvasDrawing.reset()"
      >
        {{ $t("equipment.tracker.sign.clear") }}
      </ion-button>
    </div>
    <p :class="$style['error-message']" v-if="canvaError">
      {{ $t("equipment.tracker.sign.error") }}
    </p>
    <ion-button
        v-if="tracked_event"
        :disabled="
        save_loading ||
        !item ||
        !tracked_event ||
        !used_user ||
        last_tracked_event_loading ||
        !site ||
        !item ||
        (tracked_event === 'check:in' && !photos?.length)
      "
        color="success"
        expand="block"
        fill="solid"
        size="default"
        @click="save"
    >
      {{ $t("equipment.tracker.actions." + tracked_event) }}
    </ion-button>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import {
  IonButton,
  IonIcon,
  IonLabel,
  IonRadio,
  IonRadioGroup,
  IonSpinner,
  IonTextarea,
  toastController,
} from "@ionic/vue";
import { Equipment } from "../../../equipments";
import EquipmentSelectorButton from "@/components/data/selectors/EquipmentSelectorButton.vue";
import UserSelectorButton from "@/components/data/selectors/UserSelectorButton.vue";
import { User } from "@/users";
import { useUserStore } from "@/stores/user";
import SiteSelectorButton from "@/components/data/selectors/SiteSelectorButton.vue";
import { Site } from "@/sites";
import { useEquipmentStore } from "@/stores/equipment";
import UploadableFile from "@/components/ui/fields/UploadableFile.vue";

import wrenchIcon from "iconoir/icons/regular/wrench.svg";
import deliveryIcon from "iconoir/icons/regular/delivery.svg";
import mapIcon from "iconoir/icons/regular/map.svg";
import userIcon from "iconoir/icons/regular/user.svg";
import cameraIcon from "iconoir/icons/regular/camera.svg";
import chatBubbleIcon from "iconoir/icons/regular/chat-bubble.svg";

import VueDrawingCanvas from "vue-drawing-canvas";

export default defineComponent({
  components: {
    UploadableFile,
    SiteSelectorButton,
    UserSelectorButton,
    EquipmentSelectorButton,
    IonIcon,
    IonRadio,
    IonRadioGroup,
    IonLabel,
    IonSpinner,
    IonButton,
    IonTextarea,
    VueDrawingCanvas,
  },
  props: ["equipment"],
  emits: ["close"],
  setup() {
    const user = useUserStore();
    const equipmentStore = useEquipmentStore();

    return {
      user,
      equipmentStore,
      wrenchIcon,
      deliveryIcon,
      mapIcon,
      userIcon,
      cameraIcon,
      chatBubbleIcon,
    };
  },
  data() {
    return {
      item: null as Equipment | null,
      used_user: null as User | null,
      site: null as Site | null,
      back_to_step: true,
      last_tracked_event: null as any,
      last_tracked_event_loading: true,
      tracked_event: "",
      save_loading: false,
      photos: [] as any[],
      comment: "",
      signature: "",
      steps: ["equipment", "action", "site", "user", "photos", "comment"],
      canvaError: false,

      currentStep: 0,
    };
  },
  mounted() {
    if (this.$props.equipment) {
      this.item = this.$props.equipment;
    }
  },
  watch: {
    item(value) {
      if (value) {
        this.loadLastTrackedEvent();
        this.currentStep++;
      }
    },
    site(value) {
      if (value) {
        this.currentStep++;
      }
    },
    used_user(value) {
      console.log(this.currentStep);
      if (value) {
        this.currentStep++;
      }
    },
    photos(value) {
      if (value?.length) {
        this.currentStep++;
      }
    },
    currentStep(value: number) {
      if (value >= 1) {
        if (!this.item) {
          this.currentStep = 0;
        }
      }
      if (value >= 2) {
        if (!this.tracked_event) {
          this.currentStep = 1;
        }
      }
      if (value >= 3) {
        if (!this.site) {
          this.currentStep = 2;
        }
      }
      if (value >= 4) {
        if (!this.used_user) {
          this.currentStep = 3;
        }
      }
      if (value >= 5) {
        if (this.tracked_event === "check:in" && !this.photos?.length) {
          this.currentStep = 4;
        }
      }
    },
    last_tracked_event_loading(value) {
      if (!value && this.currentStep === 1 && this.trackEvents?.length === 1) {
        this.currentStep++;
      }
    },
  },
  computed: {
    trackEvents() {
      const events = (this.equipmentStore as any)?.tracker?.events ?? [];

      if (this.last_tracked_event?.action === "check:out") {
        return events.filter((event: any) => event !== "check:out");
      } else {
        return events.filter((event: any) => event !== "check:in");
      }
    },
  },
  methods: {
    loadLastTrackedEvent() {
      if (!this.item?.uuid) return;

      this.last_tracked_event_loading = true;
      this.axios
          .get(`equipment/tracker?limit=1&uuid=${this.item.uuid}`)
          .then((response: any) => {
            this.last_tracked_event = response.data?.data?.[0] ?? null;

            if (this.trackEvents?.length) {
              this.tracked_event = this.trackEvents[0];
            }

            if (
                this.last_tracked_event?.action === "check:out" &&
                this.last_tracked_event?.site
            ) {
              this.site = this.last_tracked_event.site;
            } else {
              this.site = null;
            }

            if (
                this.last_tracked_event?.action === "check:out" &&
                this.last_tracked_event?.roles?.find(
                    (role: any) => role.role === "user"
                )
            ) {
              this.used_user = this.last_tracked_event?.roles?.find(
                  (role: any) => role.role === "user"
              )?.user;
              this.currentStep = 4;
              this.back_to_step = false;
            } else {
              this.used_user = null;
            }
          })
          .finally(() => {
            this.last_tracked_event_loading = false;
          });
    },
    isCanvaImageIsEmpty(image: string) {
      if (
        image ===
        "data:image/png;base64,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"
      )
        return true;
      else return false;
    },
    save() {
      if (this.save_loading) return;
 
      if (this.isCanvaImageIsEmpty(this.signature)) {
        this.canvaError = true;
      } else {
        this.canvaError = false;
        this.save_loading = true;
        this.axios 
          .post("equipment/tracker", {
            equipment: this.item,
            action: this.tracked_event,
            used_user: this.used_user,
            site: this.site,
            photos: this.photos,
            comment: this.comment, 
            signature: this.signature, 
          })
          .then(() => {
            const toast = toastController.create({
              message: this.$t( 
                "equipment.tracker.success." + this.tracked_event,
                {
                  equipment: this.item?.name,
                  site: this.site?.name,
                }  
              ),
              duration: 2000,
              color: "success",
            });

            toast.then((toast) => {
              toast.present();
            });

            this.$emit("close");
          })
          .catch((error: any) => {
            console.log(error);
          })
          .finally(() => {
            this.save_loading = false;
          }); 
      }
    },
  },
});
</script>

<style lang="scss" module>
.loader {
  width: 100%;
  display: flex;
  padding: 2em;
  justify-content: center;
  align-items: center;
}

.actions {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  flex-direction: column;

  padding: 1em 0;
}

.step-panel {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.step-icon {
  width: 5em;
  height: 5em;
}

.comment {
  --background: var(--ion-color-light);
  --padding-start: 1em;
  --padding-end: 1em;
  margin-bottom: 1em;
  margin-top: 1em;
}

.sign-bloc {
  border: 1px solid black;
}

.sign-bloc-button {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  margin-right: 80px;
}

.sign-bloc-error {
  border: 2px solid red;
}

.sign-bloc-clear-button {
  margin-bottom: 15px;
}
.error-message {
  color: red;
  font-size: 16px;
  margin-top: 10px;
  text-align: center;
}
</style>
